/**
 * Vector-first HTML email → pdf-lib: selectable text, embedded images at source resolution.
 * Raster fallback (html2canvas) only when explicitly requested.
 */
import type { PDFDocument, PDFPage, PDFFont } from 'pdf-lib';
import { rgb } from 'pdf-lib';
import html2canvas from 'html2canvas';
import type { EmbeddedBodyFonts } from './pdfFonts';
import type { ImageFitMode, PdfQualityLayout } from './pdfExportQuality';
import { isJpeg, isPng, webpToPngBytes } from './pdfImageBytes';

export interface HtmlPdfCursor {
  doc: PDFDocument;
  page: PDFPage;
  y: number;
  fonts: EmbeddedBodyFonts;
  layout: PdfQualityLayout;
  /** Page width in PDF points (default A4 portrait width). */
  pageWidthPt: number;
  /** Page height in PDF points (default A4 portrait height). */
  pageHeightPt: number;
}

function pageContentBottom(layout: PdfQualityLayout): number {
  return layout.margin + layout.footerReserved;
}

function freshPage(c: HtmlPdfCursor): void {
  c.page = c.doc.addPage([c.pageWidthPt, c.pageHeightPt]);
  c.y = c.pageHeightPt - c.layout.margin;
}

export function ensureVerticalHtml(c: HtmlPdfCursor, need: number): void {
  if (c.y - need < pageContentBottom(c.layout)) {
    freshPage(c);
  }
}

function pickFont(bold: boolean, italic: boolean, fonts: EmbeddedBodyFonts): PDFFont {
  if (bold && italic) return fonts.boldItalic;
  if (bold) return fonts.bold;
  if (italic) return fonts.italic;
  return fonts.regular;
}

interface Style {
  bold: boolean;
  italic: boolean;
  mono: boolean;
  sizeEm: number;
  color: ReturnType<typeof rgb>;
}

const DEFAULT_STYLE: Style = {
  bold: false,
  italic: false,
  mono: false,
  sizeEm: 1,
  color: rgb(0.12, 0.13, 0.15),
};

type Piece =
  | { type: 't'; text: string; font: PDFFont; size: number; color: ReturnType<typeof rgb> }
  | { type: 'br' }
  | { type: 'img'; src: string }
  | { type: 'hr' }
  | { type: 'pre'; text: string };

function pushText(
  out: Piece[],
  text: string,
  font: PDFFont,
  size: number,
  color: ReturnType<typeof rgb>,
): void {
  if (!text) return;
  const last = out[out.length - 1];
  if (last?.type === 't' && last.font === font && last.size === size && last.color === color) {
    last.text += text;
  } else {
    out.push({ type: 't', text, font, size, color });
  }
}

function headingMultiplier(tag: string): number {
  switch (tag.toLowerCase()) {
    case 'h1':
      return 1.62;
    case 'h2':
      return 1.48;
    case 'h3':
      return 1.34;
    case 'h4':
      return 1.22;
    case 'h5':
      return 1.12;
    case 'h6':
      return 1.06;
    default:
      return 1;
  }
}

function applyTagStyle(tag: string, st: Style): Style {
  const s: Style = { ...st };
  const t = tag.toLowerCase();
  if (t === 'strong' || t === 'b') s.bold = true;
  if (t === 'em' || t === 'i') s.italic = true;
  if (t === 'code' || t === 'kbd' || t === 'samp') s.mono = true;
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(t)) {
    s.bold = true;
    s.sizeEm = st.sizeEm * headingMultiplier(t);
  }
  if (t === 'blockquote') s.italic = true;
  if (t === 'small') s.sizeEm *= 0.88;
  return s;
}

const SKIP = new Set(['script', 'style', 'noscript', 'meta', 'link', 'title', 'head', 'template']);

const BLOCK = new Set([
  'p',
  'div',
  'section',
  'article',
  'aside',
  'header',
  'footer',
  'main',
  'nav',
  'blockquote',
  'form',
  'center',
  'figure',
  'figcaption',
  'dl',
  'dt',
  'dd',
  'table',
  'tbody',
  'thead',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'address',
  'ul',
  'ol',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

function walkNode(node: Node, st: Style, baseSize: number, fonts: EmbeddedBodyFonts, out: Piece[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const raw = node.textContent ?? '';
    if (!raw) return;
    const font = st.mono ? fonts.mono : pickFont(st.bold, st.italic, fonts);
    const size = baseSize * st.sizeEm;
    pushText(out, raw, font, size, st.color);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (SKIP.has(tag)) return;

  if (tag === 'br') {
    out.push({ type: 'br' });
    return;
  }
  if (tag === 'hr') {
    out.push({ type: 'br' });
    out.push({ type: 'hr' });
    out.push({ type: 'br' });
    return;
  }
  if (tag === 'img') {
    const src = el.getAttribute('src') || '';
    if (src) {
      out.push({ type: 'br' });
      out.push({ type: 'img', src });
      out.push({ type: 'br' });
    }
    return;
  }
  if (tag === 'pre') {
    out.push({ type: 'br' });
    out.push({ type: 'pre', text: el.textContent ?? '' });
    out.push({ type: 'br' });
    return;
  }

  const next = applyTagStyle(tag, st);
  const isBlock = BLOCK.has(tag) || tag === 'li' || tag === 'p';

  if (tag === 'li') {
    out.push({ type: 'br' });
    const symFont = pickFont(next.bold, next.italic, fonts);
    const symSize = baseSize * next.sizeEm;
    pushText(out, '•  ', symFont, symSize, next.color);
  }

  if (isBlock) out.push({ type: 'br' });

  for (const ch of Array.from(el.childNodes)) {
    walkNode(ch, next, baseSize, fonts, out);
  }

  if (isBlock) out.push({ type: 'br' });
}

interface WordTok {
  text: string;
  font: PDFFont;
  size: number;
  color: ReturnType<typeof rgb>;
}

function piecesToWords(pieces: Piece[]): WordTok[] {
  const flat: WordTok[] = [];
  for (const p of pieces) {
    if (p.type !== 't') continue;
    const re = /(\s+|\S+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(p.text)) !== null) {
      flat.push({ text: m[1], font: p.font, size: p.size, color: p.color });
    }
  }
  return flat;
}

function breakLines(words: WordTok[], maxW: number): WordTok[][] {
  const lines: WordTok[][] = [];
  let line: WordTok[] = [];
  let lw = 0;

  const flush = () => {
    if (line.length) {
      lines.push(line);
      line = [];
      lw = 0;
    }
  };

  for (const w of words) {
    if (/^\s+$/.test(w.text)) {
      if (line.length) line.push(w);
      continue;
    }
    const ww = w.font.widthOfTextAtSize(w.text, w.size);
    const spaceW =
      line.length && !/^\s+$/.test(line[line.length - 1].text)
        ? line[line.length - 1].font.widthOfTextAtSize(' ', line[line.length - 1].size)
        : 0;
    const add = (line.length ? spaceW : 0) + ww;

    if (line.length && lw + add > maxW) {
      flush();
      line.push(w);
      lw = ww;
    } else {
      line.push(w);
      lw += add;
    }
  }
  flush();
  return lines;
}

function lineHeightFor(line: WordTok[]): number {
  return Math.max(0.0001, ...line.map((w) => w.font.heightAtSize(w.size)));
}

function drawTextLine(c: HtmlPdfCursor, line: WordTok[], contentLeft: number): void {
  if (!line.length) return;
  const lh = lineHeightFor(line) * c.layout.lineHeightMult;
  ensureVerticalHtml(c, lh + 2);
  let x = contentLeft;
  let lastNonSpace: WordTok | null = null;
  for (const w of line) {
    if (/^\s+$/.test(w.text)) {
      if (lastNonSpace) x += lastNonSpace.font.widthOfTextAtSize(' ', lastNonSpace.size);
      continue;
    }
    c.page.drawText(w.text, { x, y: c.y, size: w.size, font: w.font, color: w.color });
    x += w.font.widthOfTextAtSize(w.text, w.size);
    lastNonSpace = w;
  }
  c.y -= lh;
}

async function embedDataUrlImage(
  doc: PDFDocument,
  dataUrl: string,
): Promise<{ image: Awaited<ReturnType<PDFDocument['embedPng']>> } | null> {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return null;
  const meta = dataUrl.slice(0, comma);
  const b64 = dataUrl.slice(comma + 1);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

  if (meta.includes('image/png') || isPng(bytes)) {
    return { image: await doc.embedPng(bytes) };
  }
  if (meta.includes('image/jpeg') || meta.includes('image/jpg') || isJpeg(bytes)) {
    const blob = new Blob([bytes], { type: 'image/jpeg' });
    const bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    const c = document.createElement('canvas');
    c.width = bmp.width;
    c.height = bmp.height;
    const ctx = c.getContext('2d');
    if (!ctx) {
      bmp.close();
      return { image: await doc.embedJpg(bytes) };
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bmp, 0, 0);
    bmp.close();
    const jpegBlob = await new Promise<Blob>((res, rej) => {
      c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/jpeg', 0.96);
    });
    return { image: await doc.embedJpg(new Uint8Array(await jpegBlob.arrayBuffer())) };
  }
  if (meta.includes('image/webp')) {
    const png = await webpToPngBytes(bytes);
    return { image: await doc.embedPng(png) };
  }
  try {
    return { image: await doc.embedPng(bytes) };
  } catch {
    try {
      return { image: await doc.embedJpg(bytes) };
    } catch {
      return null;
    }
  }
}

async function loadImageBitmapOriented(src: string): Promise<{ w: number; h: number } | null> {
  try {
    if (!src.startsWith('data:')) return null;
    const res = await fetch(src);
    const blob = await res.blob();
    const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    const out = { w: bitmap.width, h: bitmap.height };
    bitmap.close();
    return out;
  } catch {
    return null;
  }
}

function computeImageDrawSize(
  imgW: number,
  imgH: number,
  fit: ImageFitMode,
  maxW: number,
  maxH: number,
): { w: number; h: number } {
  if (fit === 'original') {
    const wPt = imgW * (72 / 96);
    const hPt = imgH * (72 / 96);
    if (wPt <= maxW && hPt <= maxH) return { w: wPt, h: hPt };
    const r = Math.min(maxW / wPt, maxH / hPt);
    return { w: wPt * r, h: hPt * r };
  }
  if (fit === 'fitWidth') {
    const r = Math.min(1, maxW / imgW);
    return { w: imgW * r, h: imgH * r };
  }
  const r = Math.min(maxW / imgW, maxH / imgH, 1);
  return { w: imgW * r, h: imgH * r };
}

async function drawInlineImage(c: HtmlPdfCursor, src: string, imageFit: ImageFitMode): Promise<void> {
  if (!src.startsWith('data:')) return;

  const m = c.layout.margin;
  const contentW = c.pageWidthPt - 2 * m;
  const maxH = c.y - pageContentBottom(c.layout);

  const oriented = await loadImageBitmapOriented(src);
  const effW = oriented?.w ?? 800;
  const effH = oriented?.h ?? 600;
  const { w, h } = computeImageDrawSize(effW, effH, imageFit, contentW, Math.max(72, maxH));

  ensureVerticalHtml(c, h + 12);
  const embedded = await embedDataUrlImage(c.doc, src);
  if (!embedded) return;

  const yBottom = c.y - h;
  c.page.drawImage(embedded.image, { x: m, y: yBottom, width: w, height: h });
  c.y = yBottom - 8;
}

function drawHorizontalRule(c: HtmlPdfCursor): void {
  ensureVerticalHtml(c, 16);
  const m = c.layout.margin;
  c.page.drawLine({
    start: { x: m, y: c.y },
    end: { x: c.pageWidthPt - m, y: c.y },
    thickness: 0.55,
    color: rgb(0.86, 0.87, 0.9),
  });
  c.y -= 14;
}

function drawPreformatted(c: HtmlPdfCursor, text: string, fonts: EmbeddedBodyFonts, baseSize: number): void {
  const font = fonts.mono;
  const size = baseSize * 0.95;
  const maxW = c.pageWidthPt - 2 * c.layout.margin;
  const lines = text.split(/\r?\n/);
  const color = rgb(0.15, 0.16, 0.2);
  for (const ln of lines) {
    const words = ln.length
      ? breakLines([{ text: ln, font, size, color }], maxW)
      : [[] as WordTok[]];
    for (const wl of words) {
      drawTextLine(c, wl, c.layout.margin);
    }
  }
}

/**
 * Primary path: DOM → pdf-lib text + native image XObjects.
 */
export async function renderHtmlEmailVector(
  c: HtmlPdfCursor,
  html: string,
  imageFit: ImageFitMode,
  onYield?: () => Promise<void>,
): Promise<void> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__root');
  if (!root) return;

  const baseSize = c.layout.bodySize;
  const pieces: Piece[] = [];
  walkNode(root, DEFAULT_STYLE, baseSize, c.fonts, pieces);

  const maxW = c.pageWidthPt - 2 * c.layout.margin;
  let buf: Piece[] = [];
  let iter = 0;

  const flushTextBuffer = async () => {
    const words = piecesToWords(buf);
    buf = [];
    if (!words.length) return;
    const lines = breakLines(words, maxW);
    for (const line of lines) {
      drawTextLine(c, line, c.layout.margin);
      iter += 1;
      if (onYield && iter % 24 === 0) await onYield();
    }
  };

  for (const p of pieces) {
    if (p.type === 'br') {
      await flushTextBuffer();
      c.y -= baseSize * 0.12;
      continue;
    }
    if (p.type === 'hr') {
      await flushTextBuffer();
      drawHorizontalRule(c);
      continue;
    }
    if (p.type === 'pre') {
      await flushTextBuffer();
      drawPreformatted(c, p.text, c.fonts, baseSize);
      continue;
    }
    if (p.type === 'img') {
      await flushTextBuffer();
      await drawInlineImage(c, p.src, imageFit);
      continue;
    }
    buf.push(p);
  }
  await flushTextBuffer();
}

/**
 * Compatibility: high-resolution raster (only when vector path insufficient).
 */
export async function renderHtmlEmailRasterFallback(c: HtmlPdfCursor, html: string): Promise<void> {
  const m = c.layout.margin;
  const contentWPt = c.pageWidthPt - 2 * m;
  const pxW = Math.max(360, Math.round((contentWPt / 72) * 96));

  const container = document.createElement('div');
  container.style.cssText = [
    'position:fixed',
    'left:-20000px',
    'top:0',
    `width:${pxW}px`,
    'background:#ffffff',
    'color:#141414',
    'padding:0',
    'box-sizing:border-box',
    '-webkit-font-smoothing:antialiased',
    'font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    `font-size:${Math.round(c.layout.bodySize * 1.33)}px`,
    `line-height:${c.layout.lineHeightMult.toFixed(2)}`,
    'word-break:break-word',
  ].join(';');
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const scale = Math.max(c.layout.rasterScale, dpr);
    const canvas = await html2canvas(container, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const imgWPt = contentWPt;
    const imgScale = imgWPt / canvas.width;
    const totalDrawH = canvas.height * imgScale;
    let drawn = 0;

    while (drawn < totalDrawH) {
      const room = c.y - pageContentBottom(c.layout);
      if (room < 24) {
        freshPage(c);
        continue;
      }
      const chunkDraw = Math.min(room, totalDrawH - drawn);
      const srcH = chunkDraw / imgScale;
      const srcY = drawn / imgScale;

      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = Math.max(1, Math.ceil(srcH));
      const sctx = slice.getContext('2d');
      if (!sctx) throw new Error('2d context unavailable');
      sctx.imageSmoothingEnabled = true;
      sctx.imageSmoothingQuality = 'high';
      sctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

      const pngBlob = await new Promise<Blob>((res, rej) =>
        slice.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/png', 0.85),
      );
      const pngBytes = new Uint8Array(await pngBlob.arrayBuffer());
      const img = await c.doc.embedPng(pngBytes);
      ensureVerticalHtml(c, chunkDraw + 6);
      c.page.drawImage(img, { x: m, y: c.y - chunkDraw, width: imgWPt, height: chunkDraw });
      c.y -= chunkDraw + 8;
      drawn += chunkDraw;
    }
  } finally {
    document.body.removeChild(container);
  }
}
